import { getIntlLocale } from '@/shared/i18n/store';

/**
 * The app's display locale = the UI language (`shared/i18n`). Dates,
 * weekdays, months, numbers and money are formatted in the language the
 * labels are in — never in the browser's own locale (an English UI on a
 * Russian-locale browser must not show "сб вс пн" or "0,00 $"). Every
 * `toLocale*String` / `Intl.*Format` call in `pages/` and `widgets/` passes
 * `appLocale()` or uses a helper below; `src/__arch__/locale.spec.ts` keeps it
 * that way.
 *
 * It is a FUNCTION, read at format time: never capture it in a module-level
 * constant or formatter (that would freeze the language at import).
 *
 * Time ZONES are a separate concern: helpers take an optional `timeZone`, and
 * calendar dates (`YYYY-MM-DD`) are formatted at UTC noon so the day never
 * shifts.
 */
export function appLocale(): string {
  return getIntlLocale();
}

type DateInput = Date | string | number;

function toDate(d: DateInput): Date {
  if (d instanceof Date) return d;
  // A bare calendar date reads as that day everywhere (UTC noon, formatted in UTC).
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return new Date(`${d}T12:00:00Z`);
  return new Date(d);
}

function isCalendarDate(d: DateInput): boolean {
  return typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d);
}

/** Format with explicit options in the app locale. */
export function fmtDate(
  d: DateInput,
  opts: Intl.DateTimeFormatOptions,
  timeZone?: string | undefined,
): string {
  const date = toDate(d);
  if (Number.isNaN(date.getTime())) return '';
  const tz = timeZone ?? (isCalendarDate(d) ? 'UTC' : undefined);
  return new Intl.DateTimeFormat(appLocale(), { ...opts, ...(tz ? { timeZone: tz } : {}) }).format(
    date,
  );
}

/** "Mon". */
export function fmtWeekday(d: DateInput, timeZone?: string): string {
  return fmtDate(d, { weekday: 'short' }, timeZone);
}

/** "Oct 12". */
export function fmtShortDate(d: DateInput, timeZone?: string): string {
  return fmtDate(d, { month: 'short', day: 'numeric' }, timeZone);
}

/** "Mon, Oct 12". */
export function fmtWeekdayDate(d: DateInput, timeZone?: string): string {
  return fmtDate(d, { weekday: 'short', month: 'short', day: 'numeric' }, timeZone);
}

/** "Oct 12, 2026". */
export function fmtLongDate(d: DateInput, timeZone?: string): string {
  return fmtDate(d, { month: 'short', day: 'numeric', year: 'numeric' }, timeZone);
}

/** "Oct 12, 2026, 3:40 PM". */
export function fmtDateTime(d: DateInput, timeZone?: string): string {
  return fmtDate(
    d,
    { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' },
    timeZone,
  );
}

/** "3:40 PM". */
export function fmtTime(d: DateInput, timeZone?: string): string {
  return fmtDate(d, { hour: 'numeric', minute: '2-digit' }, timeZone);
}

/** "26 Sep – 2 Oct" style range label for two calendar dates (both inclusive). */
export function fmtDateRange(from: DateInput, toInclusive: DateInput): string {
  const a = toDate(from);
  const b = toDate(toInclusive);
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear();
  const left = fmtDate(
    from,
    sameYear
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
  );
  const right = fmtDate(toInclusive, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${left} – ${right}`;
}

/** "1,234". */
export function fmtNumber(n: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(appLocale(), opts).format(n);
}

/** "$1,234.50" for an amount in major units. */
export function fmtMoney(
  amount: number,
  currency: string,
  opts?: Intl.NumberFormatOptions,
): string {
  try {
    return new Intl.NumberFormat(appLocale(), { style: 'currency', currency, ...opts }).format(
      amount,
    );
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/**
 * Weekday column headers in the UI language, starting on Monday (`'mon'`) or
 * Sunday: ["Mon", "Tue", …] / ["пн", "вт", …]. `narrow` gives one letter.
 */
export function fmtWeekdayNames(
  weekStart: 'mon' | 'sun' = 'mon',
  width: 'short' | 'narrow' | 'long' = 'short',
): string[] {
  // 2024-01-01 was a Monday; 2023-12-31 a Sunday.
  const first = weekStart === 'mon' ? Date.UTC(2024, 0, 1, 12) : Date.UTC(2023, 11, 31, 12);
  return Array.from({ length: 7 }, (_, i) =>
    fmtDate(new Date(first + i * 86_400_000), { weekday: width }, 'UTC'),
  );
}

/** "3 hours ago" / "через 2 дня" — relative time in the UI language. */
export function fmtRelative(value: number, unit: Intl.RelativeTimeFormatUnit, opts?: Intl.RelativeTimeFormatOptions): string {
  return new Intl.RelativeTimeFormat(appLocale(), { numeric: 'auto', ...opts }).format(value, unit);
}

/** "A, B and C" / "A, B и C". */
export function fmtList(items: readonly string[], type: 'conjunction' | 'disjunction' = 'conjunction'): string {
  return new Intl.ListFormat(appLocale(), { style: 'long', type }).format(items);
}
